'use client'

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styles from '../styles/PurchaseFlow.module.css';
import Image from 'next/image';
import { useAppKitAccount, useAppKit } from '@reown/appkit/react';
import { appKitModal } from '../../context';

// Update the CryptoCurrency type to remove SOL
export type CryptoCurrency = 'ETH' | 'USDT' | 'USDC' | 'MATIC' | 'ETH_BASE' | 'USDC_BASE';

// Network types removing Solana
export type NetworkType = 'ethereum' | 'polygon' | 'base';

// Data is static, only need info for display
interface CryptoDisplayInfo {
  name: string;
  iconUrl: string;
  color: string;
  network: NetworkType;
}

// Renamed to coinDisplayInfo to avoid duplicate declaration
const coinDisplayInfo: Record<CryptoCurrency, CryptoDisplayInfo> = {
  'ETH': { name: 'Ethereum', iconUrl: '/images/crypto/eth.svg', color: '#627EEA', network: 'ethereum' },
  'USDT': { name: 'Tether', iconUrl: '/images/crypto/usdt.svg', color: '#26A17B', network: 'ethereum' },
  'USDC': { name: 'USD Coin', iconUrl: '/images/crypto/usdc.svg', color: '#2775CA', network: 'ethereum' },
  'MATIC': { name: 'Polygon', iconUrl: '/images/crypto/matic.svg', color: '#8247E5', network: 'polygon' },
  'ETH_BASE': { name: 'Ethereum (Base)', iconUrl: '/images/crypto/base.svg', color: '#0052FF', network: 'base' },
  'USDC_BASE': { name: 'USD Coin (Base)', iconUrl: '/images/crypto/usdc.svg', color: '#2775CA', network: 'base' }
};

// Network display info
const networkDisplayInfo: Record<NetworkType, { name: string; iconUrl: string; color: string }> = {
  'ethereum': { name: 'Ethereum Network', iconUrl: '/images/crypto/eth.svg', color: '#627EEA' },
  'polygon': { name: 'Polygon Network', iconUrl: '/images/crypto/matic.svg', color: '#8247E5' },
  'base': { name: 'Base Network', iconUrl: '/images/crypto/base.svg', color: '#0052FF' }
};

interface SelectCryptoStepProps {
  onPrevious: () => void;
  onCryptoSelected: (crypto: CryptoCurrency) => void;
}

interface CryptoDisplayData {
  id: CryptoCurrency;
  network: NetworkType;
  name: string;
  symbol: string;
  logoPath: string;
  description: string;
}

// This array is used for display (keeping original name)
const cryptoDisplayData: CryptoDisplayData[] = [
  {
    id: 'ETH',
    network: 'ethereum',
    name: 'Ethereum',
    symbol: 'ETH',
    logoPath: '/images/crypto/eth.svg',
    description: 'Pay with ETH on Ethereum Mainnet'
  },
  {
    id: 'USDT',
    network: 'ethereum',
    name: 'Tether',
    symbol: 'USDT',
    logoPath: '/images/crypto/usdt.svg',
    description: 'Pay with USDT on Ethereum Mainnet'
  },
  {
    id: 'USDC',
    network: 'ethereum',
    name: 'USD Coin',
    symbol: 'USDC',
    logoPath: '/images/crypto/usdc.svg',
    description: 'Pay with USDC on Ethereum Mainnet'
  },
  {
    id: 'MATIC',
    network: 'polygon',
    name: 'Polygon',
    symbol: 'MATIC',
    logoPath: '/images/crypto/matic.svg',
    description: 'Pay with MATIC on Polygon Network'
  },
  {
    id: 'ETH_BASE',
    network: 'base',
    name: 'Ethereum (Base)',
    symbol: 'ETH',
    logoPath: '/images/crypto/base.svg',
    description: 'Pay with ETH on Base Network'
  },
  {
    id: 'USDC_BASE',
    network: 'base',
    name: 'USD Coin (Base)',
    symbol: 'USDC',
    logoPath: '/images/crypto/usdc.svg',
    description: 'Pay with USDC on Base Network'
  }
];

// Dictionary of networks with their display data
const networkDisplayData: Record<NetworkType, { name: string; logoPath: string }> = {
  ethereum: {
    name: 'Ethereum',
    logoPath: '/images/crypto/eth.svg',
  },
  polygon: {
    name: 'Polygon',
    logoPath: '/images/crypto/matic.svg',
  },
  base: {
    name: 'Base',
    logoPath: '/images/crypto/base.svg',
  }
};

// Helper function to check compatibility - adjust type signature
const isNetworkCompatible = (network: NetworkType, walletType: 'evm' | null): boolean => {
  if (!walletType) return true; // Always compatible if no wallet connected yet
  if (walletType === 'evm' && (network === 'ethereum' || network === 'polygon' || network === 'base')) return true;
  return false;
};

// Use standard function component definition before wrapping with React.memo
const SelectCryptoStepComponent = ({ onPrevious, onCryptoSelected }: SelectCryptoStepProps) => {
  console.log('Rendering SelectCryptoStepComponent');
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get AppKit functions for opening/closing modal UI
  const { open } = useAppKit(); 

  // Get connected wallet info using AppKit hook
  const { isConnected, caipAddress } = useAppKitAccount();

  // Determine connected wallet type - correctly typed as 'evm' | null
  const connectedWalletType: 'evm' | null = useMemo(() => {
    if (!isConnected || !caipAddress) return null;
    if (caipAddress.startsWith('eip155:')) return 'evm';
    console.warn('Unknown CAIP address format:', caipAddress);
    return null; 
  }, [isConnected, caipAddress]);

  // Group cryptocurrencies by network - remove Solana
  const cryptosByNetwork: Record<NetworkType, CryptoCurrency[]> = {
    'ethereum': ['ETH', 'USDT', 'USDC'],
    'polygon': ['MATIC'],
    'base': ['ETH_BASE', 'USDC_BASE']
  };

  // Group cryptos by network - fixed to iterate over array properly
  const cryptosByNetworkData = useMemo(() => {
    // Use Object.entries to create an object grouped by network
    return cryptoDisplayData.reduce((acc, crypto) => {
      if (!acc[crypto.network]) {
        acc[crypto.network] = [];
      }
      acc[crypto.network].push(crypto);
      return acc;
    }, {} as Record<NetworkType, CryptoDisplayData[]>);
  }, []);

  // Available networks
  const networks = useMemo(() => {
    return Object.keys(cryptosByNetworkData) as NetworkType[];
  }, [cryptosByNetworkData]);

  // --- Handlers --- 
  const handleSwitchWalletType = useCallback(() => {
    console.log('Switching wallet type: disconnecting and opening modal');
    setError(null); // Clear the error message
    // Use the imported modal instance to disconnect
    appKitModal.disconnect(); 
    
    // Add a small delay before opening to ensure disconnect completes
    setTimeout(() => {
      open(); 
    }, 150); // Slightly longer delay
  }, [open]); // Dependency is now only 'open'

  // Update handleNetworkSelect to remove Solana logic
  const handleNetworkSelect = useCallback((network: NetworkType) => {
    setError(null);
    if (isConnected && !isNetworkCompatible(network, connectedWalletType)) {
      // Remove Solana specific message parts
      const requiredType = 'EVM (e.g., MetaMask)';
      const networkName = networkDisplayInfo[network]?.name || network;
      setError(`Incorrect Wallet Type: To use the ${networkName}, please disconnect your current wallet and reconnect using a ${requiredType} wallet via the main connection button.`);
      return;
    }
    setSelectedNetwork(network);
  }, [isConnected, connectedWalletType]); 

  // Update handleCryptoClick to remove Solana logic
  const handleCryptoClick = useCallback((crypto: CryptoDisplayData) => {
    setError(null);
    if (isConnected && !isNetworkCompatible(crypto.network, connectedWalletType)) {
      // Remove Solana specific message parts
      const requiredType = 'EVM (e.g., MetaMask)';
      setError(`Incorrect Wallet Type: To pay with ${crypto.symbol}, please disconnect your current wallet and reconnect using a ${requiredType} wallet via the main connection button.`);
      return;
    }
    console.log(`SelectCryptoStep: ${crypto.id} selected`);
    onCryptoSelected(crypto.id);
  }, [isConnected, connectedWalletType, onCryptoSelected]);

  // Correctly define handleBackClick within the component scope
  const handleBackClick = useCallback(() => {
    setError(null); 
    if (selectedNetwork) {
      setSelectedNetwork(null); // Go back to network selection
    } else {
      onPrevious(); // Go back to the previous step in PurchaseFlow
    }
  }, [selectedNetwork, onPrevious]); // Add dependencies

  // --- Render Logic --- 
  return (
    <div className={styles.purchaseStep}>
      <button onClick={handleBackClick} className={styles.backButton}>
        ← {selectedNetwork ? 'Back to Networks' : 'Back'}
      </button>
      
      {/* Use optional chaining for selectedNetwork name */}
      <h3>{selectedNetwork ? `Select ${networkDisplayInfo[selectedNetwork]?.name || 'Token'}` : 'Select Network'}</h3>
      
      <div className={styles.infoBox}>
        <p>
          {selectedNetwork 
            ? `Select your preferred token on the ${networkDisplayInfo[selectedNetwork]?.name || networkDisplayInfo[selectedNetwork]}.` // Added fallback
            : 'Select the blockchain network for your payment.'}
          {isConnected && connectedWalletType && (
            <span style={{ display: 'block', marginTop: '5px', fontSize: '0.9em', opacity: 0.8 }}>
              {/* Remove Solana specific text */}
              (Connected with an EVM wallet)
            </span>
          )}
        </p>
      </div>
      
      {/* Display error and switch button */} 
      {error && (
        <div className={styles.errorContainer} style={{marginBottom: '1rem'}}>
          <div className={styles.errorBox}>{error}</div>
          <button
            onClick={handleSwitchWalletType}
            className={`${styles.button} ${styles.switchWalletButton}`}
            style={{ marginTop: '10px', width: '100%' }}
          >
            Switch Wallet Type
          </button>
        </div>
      )}

      {!selectedNetwork ? (
        // Show network selection
        <div className={styles.cryptoList}>
          {/* Use Object.entries safely */}
          {Object.entries(networkDisplayInfo).map(([key, data]) => {
            const network = key as NetworkType;
            const compatible = isNetworkCompatible(network, connectedWalletType);
            return (
              <button 
                key={network}
                className={`${styles.cryptoButton} ${isConnected && !compatible ? styles.incompatible : ''}`}
                onClick={() => handleNetworkSelect(network)}
                title={isConnected && !compatible ? `Requires an EVM wallet` : data.name}
              >
                <span className={styles.cryptoIcon} style={{ backgroundColor: data.color }}>
                  <Image 
                    src={data.iconUrl}
                    alt={network}
                    width={24}
                    height={24}
                  />
                </span>
                <span className={styles.cryptoName}>{data.name}</span>
              </button>
            );
          })}
        </div>
      ) : (
        // Show cryptocurrencies for selected network
        <div className={styles.cryptoList}>
          {/* Use optional chaining for safety */}
          {cryptosByNetworkData[selectedNetwork]?.map(crypto => {
            const data = coinDisplayInfo[crypto.id];
            const compatible = isNetworkCompatible(crypto.network, connectedWalletType);
            return (
              <button 
                key={crypto.id}
                className={`${styles.cryptoButton} ${isConnected && !compatible ? styles.incompatible : ''}`}
                onClick={() => handleCryptoClick(crypto)}
                title={isConnected && !compatible ? `Requires an EVM wallet` : `${data.name} (${crypto.id})`}
                disabled={isConnected && !compatible}
              >
                <span className={styles.cryptoIcon} style={{ backgroundColor: data.color }}>
                  <Image 
                    src={data.iconUrl}
                    alt={crypto.name}
                    width={24}
                    height={24}
                  />
                </span>
                <span className={styles.cryptoName}>{data.name} ({crypto.id})</span>
              </button>
            );
          }) || <p>No tokens available for this network.</p>}
        </div>
      )}
    </div>
  );
};

// Wrap the component with React.memo after defining it
export const SelectCryptoStep = React.memo(SelectCryptoStepComponent);

// Add display name for easier debugging
SelectCryptoStep.displayName = 'SelectCryptoStep'; 